import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, PhoneOutgoing, PhoneIncoming, PhoneMissed, Video } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { api } from "../lib/api";
import { BottomSheet } from "./BottomSheet";
import { useCall } from "../context/CallContext";

const ago = (d) => { try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ""; } };

const META = {
  outgoing: { answered: ["Outgoing", PhoneOutgoing, "var(--text-secondary)"], canceled: ["Cancelled", PhoneOutgoing, "var(--text-muted)"], missed: ["No answer", PhoneMissed, "var(--destructive)"], declined: ["Declined", PhoneMissed, "var(--destructive)"] },
  incoming: { answered: ["Incoming", PhoneIncoming, "var(--text-secondary)"], missed: ["Missed", PhoneMissed, "var(--destructive)"], declined: ["Declined", PhoneMissed, "var(--destructive)"] },
};

export function CallHistorySheet({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { startCall } = useCall();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/calls/history"); setItems(data); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    const h = () => { if (open) load(); };
    window.addEventListener("fv-refresh-calls", h);
    return () => window.removeEventListener("fv-refresh-calls", h);
  }, [open, load]);

  const callBack = (it) => { onClose(); setTimeout(() => startCall(it.peer_id, it.peer_name), 250); };

  return (
    <BottomSheet open={open} onClose={onClose} title="Recent Calls" height="94dvh">
      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="animate-spin gold-text" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm py-8 text-center" style={{ color: "var(--text-muted)" }}>No calls yet. Start a video call from any member's profile.</p>
      ) : (
        <div className="space-y-2" data-testid="call-history-list">
          {items.map((it) => {
            const meta = (META[it.direction] || {})[it.status] || ["Call", PhoneOutgoing, "var(--text-secondary)"];
            const Icon = meta[1];
            return (
              <div key={it.id} data-testid={`call-${it.id}`} className="flex items-center gap-3 rounded-xl px-3 py-2.5" style={{ background: "var(--overlay)" }}>
                <div className="h-10 w-10 rounded-full grid place-items-center shrink-0 gold-border overflow-hidden" style={{ background: "var(--bg-elevated)" }}>
                  <span className="font-serif-display gold-text">{(it.peer_name || "?").charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{it.peer_name}</div>
                  <div className="flex items-center gap-1.5">
                    <Icon size={12} style={{ color: meta[2] }} />
                    <span className="text-[11px]" style={{ color: meta[2] }}>{meta[0]}</span>
                    <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>· {ago(it.created_at)}</span>
                  </div>
                </div>
                <button onClick={() => callBack(it)} data-testid={`call-back-${it.peer_id}`}
                  className="h-10 w-10 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }} title="Call back">
                  <Video size={17} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </BottomSheet>
  );
}
